import React, { useState, useEffect, useCallback, createContext, useContext, useMemo } from 'react';
import axios from "axios";
import { Navigate } from "react-router-dom";
import { API } from "@/api";

const AuthContext = createContext(null);

// Use sessionStorage for better security (clears on browser close)
// In production, consider migrating to httpOnly cookies
const TOKEN_KEY = 'token';
const getStoredToken = () => sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY);
const setStoredToken = (token) => {
  sessionStorage.setItem(TOKEN_KEY, token);
  // Also keep in localStorage for persistence across tabs (optional)
  localStorage.setItem(TOKEN_KEY, token);
};
const removeStoredToken = () => {
  sessionStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(TOKEN_KEY);
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(getStoredToken());
  const [loading, setLoading] = useState(true);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pushEnabled, setPushEnabled] = useState(false);

  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  const subscribeToPush = useCallback(async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      return false;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      let subscription = await registration.pushManager.getSubscription();
      
      if (!subscription) {
        const vapidPublicKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
        
        if (!vapidPublicKey) {
          return false;
        }

        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
        });
      }

      await axios.post(`${API}/push/subscribe`, {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('p256dh')))),
          auth: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('auth'))))
        }
      });

      setPushEnabled(true);
      return true;
    } catch (error) {
      return false;
    }
  }, []);

  const requestNotificationPermission = useCallback(async () => {
    if (!('Notification' in window)) {
      return false;
    }

    if (Notification.permission === 'granted') {
      return subscribeToPush();
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        return subscribeToPush();
      }
    }

    return false;
  }, [subscribeToPush]);

  const refreshAccessToken = useCallback(async () => {
    const storedToken = getStoredToken();
    if (!storedToken) return false;
    try {
      axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      const res = await axios.post(`${API}/auth/refresh`);
      const newToken = res.data.access_token;
      setStoredToken(newToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      setToken(newToken);
      setUser(res.data.user);
      return true;
    } catch (error) {
      return false;
    }
  }, []);

  const verifyAndRefreshAuth = useCallback(async () => {
    const storedToken = getStoredToken();
    if (storedToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      try {
        const res = await axios.get(`${API}/auth/me`);
        setUser(res.data);
        setToken(storedToken);
        return true;
      } catch (error) {
        if (error.response && error.response.status === 401) {
          const refreshed = await refreshAccessToken();
          if (!refreshed) {
            removeStoredToken();
            setToken(null);
            setUser(null);
            delete axios.defaults.headers.common['Authorization'];
          }
          return refreshed;
        }
        return false;
      }
    }
    return false;
  }, [refreshAccessToken]);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        await verifyAndRefreshAuth();
      }
      setLoading(false);
    };
    initAuth();
  }, [token, verifyAndRefreshAuth]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const storedToken = getStoredToken();
        if (storedToken) {
          axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
          verifyAndRefreshAuth();
        }
      }
    };

    const handleOnline = () => {
      setIsOnline(true);
      verifyAndRefreshAuth();
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [verifyAndRefreshAuth]);

  useEffect(() => {
    let isRefreshing = false;
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (!error.response && getStoredToken()) {
          await new Promise(resolve => setTimeout(resolve, 1000));
          return axios.request(error.config);
        }
        if (error.response && error.response.status === 401 && !error.config._retry) {
          if (!isRefreshing) {
            isRefreshing = true;
            error.config._retry = true;
            const refreshed = await refreshAccessToken();
            isRefreshing = false;
            if (refreshed) {
              error.config.headers['Authorization'] = `Bearer ${getStoredToken()}`;
              return axios.request(error.config);
            }
            removeStoredToken();
            setToken(null);
            setUser(null);
            delete axios.defaults.headers.common['Authorization'];
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [refreshAccessToken]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    setStoredToken(res.data.access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.access_token}`;
    setToken(res.data.access_token);
    setUser(res.data.user);
    setTimeout(() => requestNotificationPermission(), 1000);
    return res.data;
  };

  const register = async (name, email, password) => {
    const res = await axios.post(`${API}/auth/register`, { name, email, password });
    setStoredToken(res.data.access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.access_token}`;
    setToken(res.data.access_token);
    setUser(res.data.user);
    setTimeout(() => requestNotificationPermission(), 1000);
    return res.data;
  };

  const logout = () => {
    removeStoredToken();
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setPushEnabled(false);
  };

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({ 
    user, token, login, register, logout, loading, isOnline, 
    verifyAndRefreshAuth, pushEnabled, requestNotificationPermission 
  }), [user, token, loading, isOnline, pushEnabled, verifyAndRefreshAuth, requestNotificationPermission]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="text-4xl mb-4">💪</div>
          <div className="text-gray-500">Caricamento...</div>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};
